import { OfferDiscount, OfferItem, OfferItemType, OfferUnitType } from "src/app/domains/internal";

export class OfferItemGroup {
  offer_item_group_id: string;
  name: string;
  group_type: string; // BASE | AMENDMENT
  default_margin: number;
  offer_groups: OfferItemGroup[];
  offer_items: OfferItem[];
  isPageBreakBefore: boolean;
  isExpanded: boolean;
  color: string;

  /**
   * Groupe vendu comme un forfait : le document client n'en montre que le titre, les
   * positions de texte et le total cumulé. Le chiffrage reste intact côté interne.
   */
  isDetailsHidden: boolean = false;

  constructor(name, group_type) {
    this.offer_item_group_id = generateObjectId();
    this.name = name;
    this.group_type = group_type;
    this.default_margin = 25;
    this.offer_groups = [];
    this.offer_items = [];
    this.isExpanded = true;
  }

  public static createInstance(jsonData: OfferItemGroup): OfferItemGroup;
  public static createInstance(jsonData: OfferItemGroup, offerDiscounts: any[]): OfferItemGroup;
  public static createInstance(jsonData: OfferItemGroup, offerDiscounts?: any[]): OfferItemGroup {
    const d = Object.assign(new OfferItemGroup("", ""), jsonData);
    if (d.offer_groups) {
      d.offer_groups = OfferItemGroup.createInstances(d.offer_groups, offerDiscounts);
    }
    if (d.offer_items) {
      d.offer_items = OfferItem.createInstances(d.offer_items, offerDiscounts || []);
    }
    return d;
  }

  public static createInstances(jsonData: any[]): OfferItemGroup[];
  public static createInstances(jsonData: any[], offerDiscounts: any[]): OfferItemGroup[];
  public static createInstances(jsonData: any[], offerDiscounts?: any[]): OfferItemGroup[] {
    if (!jsonData) return [];
    return jsonData.map((val) => {
      return OfferItemGroup.createInstance(val, offerDiscounts);
    });
  }

  replaceAllIds() {
    this.offer_item_group_id = generateObjectId();
    this.offer_groups.forEach((x) => x.replaceAllIds());
    this.offer_items.forEach((x) => x.replaceIds());
  }

  getPercentGroups() {
    let previousItems: OfferItem[] = [];
    let percentsArray = [];

    for (let item of this.offer_items) {
      if (item.offer_item_type === OfferItemType.PERCENT) {
        previousItems.push(item);

        percentsArray.push([...previousItems]);

        previousItems = [];
      } else {
        previousItems.push(item);
      }
    }
    return percentsArray;
  }

  getOfferItems(filter = "*") {
    if (!this.offer_items) return [];
    return filter === "*" ? this.offer_items : this.offer_items.filter((x) => x.unit_type === filter);
  }

  /**
   * Positions de texte de tout le sous-arbre, remontées à plat. Quand le détail du groupe
   * est masqué, elles sont la seule description de l'étendue du forfait qui parvient au
   * client : les perdre parce qu'elles vivent dans un sous-groupe viderait le forfait.
   *
   * Volontairement sans cache : l'aperçu PDF n'est pas un chemin chaud, et un cache mal
   * invalidé produirait un document faux — bien pire qu'une traversée de plus.
   */
  collectPrintableTexts(): OfferItem[] {
    const texts = (this.offer_items ?? []).filter((item) => item.is_text);
    for (const group of this.offer_groups ?? []) {
      texts.push(...group.collectPrintableTexts());
    }
    return texts;
  }

  getOfferItemByID(offer_item_id) {
    if (!this.offer_items) return [];
    return this.offer_items.filter((x) => x.offer_item_id === offer_item_id);
  }

  totalcost_price(filter = "*") {
    let total = 0;
    if (this.offer_groups) {
      total += this.offer_groups.reduce((tot, item) => tot + +item.totalcost_price(filter), 0);
    }
    total += this.getOfferItems(filter).reduce((tot, item) => tot + (item.shouldCount ? +item.cost_price : 0), 0);
    return total;
  }

  totalSellingPrice(filter = "*") {
    let total = 0;
    if (this.offer_groups) {
      total += this.offer_groups.reduce((tot, item) => tot + +item.totalSellingPrice(filter), 0);
    }
    total += this.getOfferItems(filter).reduce(
      (tot, item) => tot + (item.shouldCount ? +item.sellingPriceWithDiscount : 0),
      0,
    );
    return total;
  }
  totalSellingPriceWithoutDiscount(filter = "*") {
    let total = 0;
    if (this.offer_groups) {
      total += this.offer_groups.reduce((tot, item) => tot + +item.totalSellingPriceWithoutDiscount(filter), 0);
    }
    total += this.getOfferItems(filter).reduce((tot, item) => tot + (item.shouldCount ? +item.selling_price : 0), 0);
    return total;
  }

  totalUnitQuantities(filter = "*") {
    return this.getOfferItems(filter).reduce((tot, item) => tot + (item.shouldCount ? +item.unit_quantity : 0), 0);
  }

  avgunit_price(filter = "*") {
    return this.totalcost_price(filter) / this.totalUnitQuantities(filter) || 0;
  }

  totalMarginSellingPrice(filter = "*") {
    return Math.abs((this.totalSellingPrice(filter) / this.totalcost_price(filter)) * 100 - 100) || 0;
  }

  totalSellingProfit(filter = "*") {
    let total = 0;
    if (this.offer_groups) {
      total += this.offer_groups.reduce((tot, item) => tot + +item.totalSellingProfit(filter), 0);
    }
    total += this.getOfferItems(filter).reduce((tot, item) => tot + (item.shouldCount ? +item.selling_profit : 0), 0);
    return total;
  }

  totalSellingAuctionRate(filter = "*") {
    return Math.abs((this.totalSellingPriceWithAuction(filter) / this.totalSellingPrice(filter)) * 100 - 100) || 0;
  }

  totalSellingAuction(filter = "*") {
    let total = 0;
    if (this.offer_groups) {
      total += this.offer_groups.reduce((tot, item) => tot + +item.totalSellingAuction(filter), 0);
    }
    total += this.getOfferItems(filter).reduce((tot, item) => tot + (item.shouldCount ? +item.selling_auction : 0), 0);
    return total;
  }

  totalSellingPriceWithAuction(filter = "*") {
    let total = 0;
    if (this.offer_groups) {
      total += this.offer_groups.reduce((tot, item) => tot + +item.totalSellingPriceWithAuction(filter), 0);
    }
    total += this.getOfferItems(filter).reduce(
      (tot, item) => tot + (item.shouldCount ? +item.selling_price_with_auction : 0),
      0,
    );
    return total;
  }

  /** Comme totalSellingPriceWithAuction(WORKFORCE), mais compte aussi la part MO des packs. */
  totalWorkforceSellingPriceWithAuction(): number {
    let total = 0;
    if (this.offer_groups) {
      total += this.offer_groups.reduce((tot, item) => tot + +item.totalWorkforceSellingPriceWithAuction(), 0);
    }
    total += (this.offer_items ?? []).reduce((tot, item) => tot + item.workforceSellingPriceWithAuction, 0);
    return total;
  }

  totalSupplierDiscountRate(filter = "*") {
    return Math.abs((this.totalcost_priceWithDiscount(filter) / this.totalcost_price(filter)) * 100 - 100) || 0;
  }

  totalSupplierDiscount(filter = "*") {
    let total = 0;
    if (this.offer_groups) {
      total += this.offer_groups.reduce((tot, item) => tot + +item.totalSupplierDiscount(filter), 0);
    }
    total += this.getOfferItems(filter).reduce(
      (tot, item) => tot + (item.shouldCount ? +item.supplier_discount_goal_total : 0),
      0,
    );
    return total;
  }

  totalcost_priceWithDiscount(filter = "*") {
    let total = 0;
    if (this.offer_groups) {
      total += this.offer_groups.reduce((tot, item) => tot + +item.totalcost_priceWithDiscount(filter), 0);
    }
    total += this.getOfferItems(filter).reduce((tot, item) => tot + (item.shouldCount ? +item.buy_price_goal : 0), 0);
    return total;
  }

  total_billing_amount(situationIndex, filter = "*") {
    let total = 0;
    if (this.offer_groups) {
      total += this.offer_groups.reduce((tot, item) => tot + +item.total_billing_amount(situationIndex, filter), 0);
    }
    total += this.getOfferItems(filter).reduce(
      (tot, item) => tot + (item.shouldCount ? +item.billing_amount(situationIndex) : 0),
      0,
    );
    return total;
  }

  total_billing_percent(situationIndex, filter = "*") {
    const total_selling = this.totalSellingPrice(filter);
    const total_billing = this.total_billing_amount(situationIndex, filter);
    if (total_selling == 0) return 0;

    return (total_billing / total_selling) * 100;
  }

  updateSupplierDiscountGoal(supplier_id: string, offer_unit_type: OfferUnitType, val: number) {
    for (let offer_group of this.offer_groups) {
      offer_group.updateSupplierDiscountGoal(supplier_id, offer_unit_type, val);
    }
    for (let offer_item of this.getOfferItems("*").filter(
      (x) => x.supplier_id === supplier_id && x.unit_type === offer_unit_type,
    )) {
      offer_item.setSupplierDiscountGoal(val);
    }

    for (let offer_item of this.getOfferItems("*")) {
      if (offer_item.unit_type === OfferUnitType.PACK) {
        for (let article of offer_item.articles.filter(
          (x) => x.supplier_id === supplier_id && x.article_type === offer_unit_type,
        )) {
          article.computeSellingPriceWithAuction();
          article.setSupplierDiscountGoal(val);
        }
      }
    }
  }

  updateGroupBillingPercent(value, situationIndex) {
    for (let offer_group of this.offer_groups) {
      offer_group.updateGroupBillingPercent(value, situationIndex);
    }
    for (let offer_item of this.offer_items) {
      offer_item.set_billing_percent_situations(value, situationIndex);
    }
  }

  /**
   * Dernière action de masse jouée sur ce groupe, pour qu'une ligne ajoutée ensuite hérite
   * de l'état plutôt que d'arriver avec sa MO active dans un groupe vendu en régie.
   *
   * Volontairement NON persisté : le back ne lit pas ce champ, il est donc perdu au
   * rechargement de l'offre. Assumé — une ligne ajoutée après un rechargement se règle au
   * cas par cas depuis son menu.
   */
  workforceDisabledDefault: boolean = false;

  /** Écrase l'état MO de toutes les lignes du groupe et de ses sous-groupes. */
  setWorkforceDisabled(disabled: boolean) {
    this.workforceDisabledDefault = disabled;
    for (const group of this.offer_groups ?? []) {
      group.setWorkforceDisabled(disabled);
    }
    for (const item of this.offer_items ?? []) {
      item.setWorkforceDisabled(disabled);
    }
  }

  /** Aligne une ligne fraîchement ajoutée sur la dernière action de masse du groupe. */
  applyWorkforceDefault(item: OfferItem) {
    if (!this.workforceDisabledDefault) return;
    if (!item?.hasWorkforceToToggle) return;
    item.setWorkforceDisabled(true);
  }

  setOfferDiscounts(offerDiscounts: OfferDiscount[]) {
    for (const group of this.offer_groups) {
      group.setOfferDiscounts(offerDiscounts);
    }
    for (const item of this.offer_items) {
      item.setOfferDiscounts(offerDiscounts);
    }
  }

  get_suppliers() {
    let suppliers = [];
    for (let offer_group of this.offer_groups) {
      suppliers = suppliers.concat(offer_group.get_suppliers());
    }
    for (let offer_item of this.offer_items) {
      suppliers.push(offer_item.supplier_id);
    }
    return suppliers;
  }
}

function generateObjectId(): string {
  const timestamp = Math.floor(new Date().getTime() / 1000).toString(16);
  const randomHex = Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join("");

  return timestamp + randomHex;
}
